/**
 * PaymentOfferEditor.tsx — 运营支付活动 offer 创建/编辑弹窗（ADM-PAY-S005）
 *
 * 新建+编辑合一（isEdit = !!item?.id）：
 *   - 新建：paymentOffersApi.create（{app_id, scenario, starts_at, ends_at, eligibility}）。
 *   - 编辑：paymentOffersApi.update（仅 {starts_at?, ends_at?, eligibility?}）—— scenario / app_id 是身份字段、
 *     创建后不可改（改 scenario = 换活动 → 建新 offer），编辑态只读展示。is_active 不在本表单：启停经列表
 *     「上架/下架」按钮走 activate/deactivate（offer 全写 operator+，不收窄）。
 * 时间：starts_at/ends_at 用 ~/ui DateTimeButton（日历浮层录入本地墙上时刻 "YYYY-MM-DDTHH:MM"）+ localInputToUtcIso
 *   转 UTC Z ISO（后端 z.string().datetime() 仅接受 UTC Z）；窗口硬约束 ends_at 必须严格晚于 starts_at（前端预校验 + 后端 DB CHECK）。
 * 成功 invalidate ['payment-offers', appId] + 关闭；失败 toast 不关弹窗（后端校验 / 护栏码 PAY_OFFER_REFERENCED
 *   由 message 透出）。RBAC：写动作 operator+（后端 requireRole 权威）。
 */

import type {
  CreatePaymentOfferRequest,
  OfferEligibility,
  PaymentScenario,
  UpdatePaymentOfferRequest,
} from '@novel-admin/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  ELIGIBILITY_OPTIONS,
  OFFER_SCENARIO_ITEMS,
  type OfferRow,
} from '~/data/adapters/payment-offer-adapter';
import { useApps } from '~/data/queries';
import { paymentOffersApi } from '~/hooks/use-payment-offers';
import { Icons } from '~/lib/icons';
import { useAuthStore } from '~/stores/auth-store';
import { AppIcon } from '~/ui/AppIcon';
import { DateTimeButton } from '~/ui/DateTimeButton';
import { Dialog } from '~/ui/Dialog';
import { type FormRowProps, FormRow as UIFormRow } from '~/ui/FormRow';
import { ScenarioPicker } from '~/ui/ScenarioPicker';
import { Select } from '~/ui/Select';
import { toast } from '~/ui/Toast';

const FORBIDDEN_ROLE_TITLE = '权限不足 · 该操作受角色限制（后端 requireRole 为权威）';

/** scenario → 展示中文名（= 原型 OFFER_SCENARIOS 文案；编辑态 title / 只读 chip 用）*/
const scenarioLabel = (v: string) => OFFER_SCENARIO_ITEMS.find((i) => i.value === v)?.label ?? v;

/** dialogs 惯例：FormRow 固定 labelWidth=140 / gap=14 / pad='12px 0' / labelPadTop=6 */
const FormRow = (p: FormRowProps) => (
  <UIFormRow labelWidth={140} gap={14} pad="12px 0" labelPadTop={6} {...p} />
);

/** datetime-local 墙上时刻（浏览器本地 tz，含 DST）→ UTC Z ISO（后端 z.string().datetime() 仅接受 UTC Z）*/
function localInputToUtcIso(local: string): string | null {
  if (!local) return null;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** UTC ISO → datetime-local input 值 YYYY-MM-DDTHH:mm（本地 tz；编辑态回填）*/
function utcIsoToLocalInput(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function PaymentOfferEditor({
  appId,
  item,
  onClose,
}: {
  appId: string;
  item?: OfferRow;
  onClose: () => void;
}) {
  const isEdit = !!item?.id;
  const queryClient = useQueryClient();
  const role = useAuthStore((s) => s.user?.role);
  const canWrite = role === 'root' || role === 'admin' || role === 'operator';

  // 生效 App 展示行用：从已缓存 apps 列表按 appId 解析当前 App（icon/color/name）；只读展示、不改 appId
  const { data: apps } = useApps();
  const app = apps.find((a) => a.id === appId);

  // 身份字段（编辑态只读）。创建态无默认选中（原型 useState(offer?.scenario || "")）：
  // 须操作员显式选场景才可提交，防误选默认场景直接创建
  const [scenario, setScenario] = useState<PaymentScenario | ''>(item?.scenario ?? '');
  // 窗口 + 资格（create + update 均可改）
  const [startsAt, setStartsAt] = useState(item ? utcIsoToLocalInput(item.startsAt) : '');
  const [endsAt, setEndsAt] = useState(item ? utcIsoToLocalInput(item.endsAt) : '');
  const [eligibility, setEligibility] = useState<OfferEligibility>(item?.eligibility ?? 'all');
  // is_active 不在 create/update 契约：启停经列表「上架/下架」按钮走 activate/deactivate（offer 全写 operator+，不收窄）

  // 编辑态初始 datetime-local 值（dirty 比对：未改动则 PATCH 不携带 starts_at/ends_at，避免分截断值回写、
  // 静默移动已存窗口、丢失秒/毫秒精度 —— codex §2a [P2]）
  const initialStarts = item ? utcIsoToLocalInput(item.startsAt) : '';
  const initialEnds = item ? utcIsoToLocalInput(item.endsAt) : '';

  const startsIso = localInputToUtcIso(startsAt);
  const endsIso = localInputToUtcIso(endsAt);
  const windowOk =
    !!startsIso && !!endsIso && new Date(endsIso).getTime() > new Date(startsIso).getTime();

  // 渠道落点前置校验已随 dev-docs #530 退役：契约明确允许「offer 先建、渠道后配」，写侧不校验落点存在性
  // （渠道配置与 App 级开关是 serving 侧计算输入）。原实现还拿「支付页是否上架」当 placement 的替身，
  // 本身就会误判，故整体移除而非降级为提示。

  const saveMutation = useMutation({
    mutationFn: async (): Promise<void> => {
      if (isEdit && item?.id) {
        // 编辑：scenario / app_id 不可改。eligibility 始终提交（无精度问题、保证 ≥1 字段）；starts_at/ends_at
        // 仅当操作员实际改了 datetime-local 才携带——未改动不送，避免分截断值回写、静默移动窗口（codex §2a [P2]）。
        const body: UpdatePaymentOfferRequest = { eligibility };
        if (startsAt !== initialStarts) body.starts_at = startsIso ?? undefined;
        if (endsAt !== initialEnds) body.ends_at = endsIso ?? undefined;
        await paymentOffersApi.update(item.id, body);
        return;
      }
      // startsIso/endsIso/scenario 非空由 canSubmit 保证
      const body: CreatePaymentOfferRequest = {
        app_id: appId,
        scenario: scenario as PaymentScenario,
        starts_at: startsIso as string,
        ends_at: endsIso as string,
        eligibility,
      };
      await paymentOffersApi.create(body);
    },
    onSuccess: async () => {
      toast(`已${isEdit ? '保存' : '创建'}运营支付活动`, { tone: 'pos', icon: 'IconCheck' });
      // await invalidate 在 onClose 前：刷新完成前 isPending 保持真、保存按钮不复活
      await queryClient.invalidateQueries({ queryKey: ['payment-offers', appId] });
      onClose();
    },
    onError: (err) => {
      toast(err instanceof Error ? err.message : '保存失败', { tone: 'neg', icon: 'IconAlert' });
    },
  });

  // 校验：写权限 + 起止窗口齐全且 ends>starts + 非 pending；创建态还需已选 scenario 且有「上架」渠道落点
  // （编辑态 offer 已存在、不再 gate 落点：启停经列表上架/下架按钮另行处理）。
  const canSubmit = canWrite && windowOk && (isEdit || !!scenario) && !saveMutation.isPending;

  return (
    <Dialog
      width={620}
      title={isEdit ? `编辑活动 · ${scenarioLabel(scenario)}` : '创建运营支付活动'}
      subtitle={
        isEdit
          ? '仅可修改有效期窗口与资格 · 通道场景 / offer_id 创建后不可改'
          : '选择通道场景 + 有效期窗口 + 资格 · 创建即上架（is_active=true）'
      }
      icon="IconTag"
      onClose={onClose}
      footer={
        <>
          {/* 左侧动态状态：创建态无落点 / 窗口非法 → 警示着色，否则默认提示「操作将记入操作日志」 */}
          <div
            style={{
              flex: 1,
              fontSize: 11,
              color: !windowOk && (startsAt || endsAt) ? 'var(--warn)' : 'var(--fg-muted)',
            }}
          >
            {!windowOk && (startsAt || endsAt) ? '结束时间须晚于开始时间' : '操作将记入操作日志'}
          </div>
          <button className="btn" onClick={onClose}>
            取消
          </button>
          <button
            className="btn btn-accent"
            disabled={!canSubmit}
            // 禁用原因：未选场景 / 窗口非法；权限不足优先（渠道落点前置校验已随 #530 退役）
            title={
              !canWrite
                ? FORBIDDEN_ROLE_TITLE
                : !isEdit && !scenario
                  ? '请选择通道场景'
                  : !windowOk
                    ? '请填写有效的有效期窗口'
                    : undefined
            }
            onClick={() => saveMutation.mutate()}
          >
            <Icons.IconCheck size={11} />
            {isEdit ? '保存' : '创建活动'}
          </button>
        </>
      }
    >
      <div style={{ padding: '4px 18px 8px' }}>
        {/* 生效 App：当前 App 维度只读展示（icon + 名 + mono id）· 活动按 App 维度配置、由顶部 App 选择器决定 */}
        <FormRow label="生效 App" hint="活动按 App 维度配置 · 由顶部 App 选择器决定">
          <div className="hstack" style={{ gap: 8, alignItems: 'center' }}>
            {app && <AppIcon app={app} size="sm" />}
            <span style={{ fontWeight: 500 }}>{app?.name ?? appId}</span>
            <span className="mono" style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
              {appId}
            </span>
          </div>
        </FormRow>

        {/* offer_id：编辑态展示稳定对外标识（只读 mono）+ 复制按钮；创建态保存后由后端生成 */}
        {isEdit && item?.id && (
          <FormRow label="offer_id" hint="对外稳定标识 · 创建后不可修改">
            <div className="hstack" style={{ gap: 6, alignItems: 'center' }}>
              <span className="mono" style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>
                {item.id}
              </span>
              <button
                type="button"
                className="btn btn-ghost btn-icon"
                style={{ width: 22, height: 22 }}
                title="复制 offer_id"
                onClick={() => {
                  navigator.clipboard?.writeText(item.id);
                  toast('已复制 offer_id', { tone: 'pos', icon: 'IconCheck' });
                }}
              >
                <Icons.IconCopy size={11} />
              </button>
            </div>
          </FormRow>
        )}

        <FormRow
          label="通道场景 (scenario)"
          required
          hint={isEdit ? '创建后不可修改' : '闭集 6 值 · 客户端在对应触发点请求'}
        >
          {isEdit ? (
            // 编辑态只读三件套：chip-info 中文名 + mono 原值 + 锁图标（身份字段不可改）
            <div className="hstack" style={{ gap: 8 }}>
              <span className="chip chip-info" style={{ fontSize: 10.5 }}>
                {scenarioLabel(scenario)}
              </span>
              <span className="mono" style={{ fontSize: 10.5, color: 'var(--fg-faint)' }}>
                {scenario}
              </span>
              <Icons.IconLock size={11} style={{ color: 'var(--fg-faint)' }} />
            </div>
          ) : (
            <ScenarioPicker items={OFFER_SCENARIO_ITEMS} value={scenario} onSelect={setScenario} />
          )}
        </FormRow>

        {/* 有效期窗口：单 FormRow · 两行（开始 / 结束）+ 区间说明行 · 窗口错误提示保留 */}
        <FormRow label="有效期窗口" required hint="按操作员本地时区录入 · 存储为 UTC (timestamptz)">
          <div className="vstack" style={{ gap: 6 }}>
            <div className="hstack" style={{ gap: 8 }}>
              <span style={{ fontSize: 11, color: 'var(--fg-muted)', width: 32 }}>开始</span>
              <DateTimeButton
                value={startsAt}
                onChange={setStartsAt}
                placeholder="选择开始时间"
                style={{ width: 240 }}
              />
            </div>
            <div className="hstack" style={{ gap: 8 }}>
              <span style={{ fontSize: 11, color: 'var(--fg-muted)', width: 32 }}>结束</span>
              <DateTimeButton
                value={endsAt}
                onChange={setEndsAt}
                placeholder="选择结束时间"
                style={{ width: 240 }}
              />
            </div>
            <div style={{ fontSize: 10.5, color: 'var(--fg-faint)' }}>
              区间 [开始, 结束) · 按操作员本地时区录入 · 存储为 <span className="mono">UTC</span>{' '}
              (timestamptz)
            </div>
            {startsAt && endsAt && !windowOk && (
              <span style={{ fontSize: 11, color: 'var(--neg)' }}>结束时间须晚于开始时间</span>
            )}
          </div>
        </FormRow>

        <FormRow label="资格 (eligibility)" required hint="活动面向的用户范围">
          <Select
            value={eligibility}
            onChange={(e) => setEligibility(e.target.value as OfferEligibility)}
            style={{ maxWidth: 200 }}
          >
            {ELIGIBILITY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label} · {o.value}
              </option>
            ))}
          </Select>
        </FormRow>

        {/* 创建态 info 条：活动须配有效期窗口 + 该 scenario 须有 active 渠道落点，否则 serving 死链 */}
        {!isEdit && (
          <div
            style={{
              marginTop: 10,
              padding: 10,
              background: 'var(--info-soft)',
              borderRadius: 5,
              fontSize: 11.5,
              color: 'var(--info)',
              lineHeight: 1.55,
            }}
          >
            <Icons.IconInfo size={12} style={{ verticalAlign: 'middle', marginRight: 6 }} />
            活动须配有效期窗口，<span className="mono">scenario</span>{' '}
            须属于活动可用场景。创建不检查该 scenario
            是否已配渠道落点（支付页通道）——落点未配或不可售时活动可以保存，但 serving
            落空。保存后生成稳定 <span className="mono">offer_id</span>，供 Banner / 推送携带。
          </div>
        )}
      </div>
    </Dialog>
  );
}
